import { Fragment, useCallback, useEffect, useRef } from 'react'
import type { CSSProperties, ReactNode, RefObject } from 'react'
import { motion } from '@lunary/ui-tokens'
import type { ReadingTabMarker } from '@lunary/app-core/orbit-guide'
import { Icon } from './icons'
import type { IconName } from './icons'
import { useLiquidPill } from './use-liquid-pill'
import type { PillGeometry } from './use-liquid-pill'
import type { SwipePreview } from './swipe-pager'
import styles from './tab-bar.module.css'

export type TabItem = {
  key: string
  label: string
  icon: IconName
  center?: boolean
  attention?: boolean
  marker?: ReadingTabMarker | null
}

export type TabLinkProps = {
  ref: (el: HTMLElement | null) => void
  className: string
  'aria-label': string
  'aria-current': 'page' | undefined
  'data-center': 'true' | 'false'
  children: ReactNode
}

type Props<T extends TabItem> = {
  items: readonly T[]
  activeKey: string | null
  renderLink: (item: T, props: TabLinkProps) => ReactNode
  inline?: boolean
  className?: string
  previewRef?: RefObject<SwipePreview | null>
}

const BASE = 'background-color .35s ease, border-color .35s ease, box-shadow .35s ease, top .2s ease, bottom .2s ease'
const CENTER_RADIUS = 23
const INSET = 4
const MEASURE = styles.measure ?? 'measure'

const NAV_VARS: CSSProperties & Record<`--${string}`, string> = {
  '--nav-grow': `${motion.navGrow}`,
  '--nav-grow-ms': `${motion.navGrowMs}ms`,
  '--nav-ease': motion.navEase,
  '--nav-label': `${motion.navLabelPx}px`,
}

const geometry = (bar: HTMLElement, tab: HTMLElement): PillGeometry => {
  const center = tab.dataset.center === 'true'
  const fab = tab.firstElementChild
  const radius = center && fab instanceof HTMLElement ? fab.offsetWidth / 2 : CENTER_RADIUS
  const cx = tab.offsetLeft + tab.offsetWidth / 2
  const left = center ? cx - radius : tab.offsetLeft + INSET
  const right = bar.clientWidth - (center ? cx + radius : tab.offsetLeft + tab.offsetWidth - INSET)
  return { left, right, center }
}

const growOf = (bar: HTMLElement) => getComputedStyle(bar).getPropertyValue('--nav-grow').trim() || '1'

const geometryAs = (bar: HTMLElement, tab: HTMLElement, current: HTMLElement): PillGeometry => {
  const grow = growOf(bar)
  bar.classList.add(MEASURE)
  if (tab.dataset.center !== 'true') tab.style.flexGrow = grow
  if (current.dataset.center !== 'true') current.style.flexGrow = '1'
  void bar.offsetWidth
  const g = geometry(bar, tab)
  tab.style.flexGrow = ''
  current.style.flexGrow = ''
  void bar.offsetWidth
  bar.classList.remove(MEASURE)
  return g
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

const markCenter = (pill: HTMLElement, { center }: PillGeometry) => {
  pill.classList.toggle(styles.gold ?? '', center === true)
}

function TabMarker({ marker }: { marker: ReadingTabMarker }) {
  if (marker.kind === 'wait') {
    return (
      <span className={`${styles.mark} ${styles.markWait}`} aria-hidden="true">
        <Icon name={marker.icon} size={11} stroke={2.6} />
      </span>
    )
  }
  if (marker.kind === 'ready') {
    return (
      <span className={`${styles.mark} ${styles.markCount}`} aria-hidden="true">
        {marker.count > 9 ? '9+' : marker.count}
      </span>
    )
  }
  return (
    <span className={`${styles.mark} ${styles.markWarn}`} aria-hidden="true">
      !
    </span>
  )
}

export function TabBar<T extends TabItem>({ items, activeKey, renderLink, inline = false, className, previewRef }: Props<T>) {
  const prev = useRef<HTMLElement | null>(null)

  const measure = useCallback((bar: HTMLElement, tab: HTMLElement): PillGeometry => {
    const from = prev.current
    prev.current = tab
    const grow = growOf(bar)
    const settle = () => void bar.offsetWidth
    bar.classList.add(MEASURE)
    settle()
    const g = geometry(bar, tab)
    if (from && from !== tab && bar.dataset.inline !== 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (tab.dataset.center !== 'true') tab.style.flexGrow = '1'
      if (from.dataset.center !== 'true') from.style.flexGrow = grow
      settle()
      bar.classList.remove(MEASURE)
      settle()
      tab.style.flexGrow = ''
      from.style.flexGrow = ''
    } else {
      bar.classList.remove(MEASURE)
    }
    return g
  }, [])

  const { pillRef, register, itemOf } = useLiquidPill<HTMLDivElement>(activeKey, { base: BASE, squash: 5, measure, onPlace: markCenter })

  useEffect(() => {
    if (!previewRef) return undefined
    let state: { to: string; a: PillGeometry; b: PillGeometry } | null = null
    previewRef.current = {
      move: (from, to, progress) => {
        const pill = pillRef.current
        const bar = pill?.parentElement
        const a = itemOf(from)
        const b = itemOf(to)
        if (!pill || !bar || !a || !b) return
        if (state?.to !== to) state = { to, a: geometry(bar, a), b: geometryAs(bar, b, a) }
        const p = Math.min(1, Math.max(0, progress))
        pill.style.transition = BASE
        pill.style.left = `${lerp(state.a.left, state.b.left, p)}px`
        pill.style.right = `${lerp(state.a.right, state.b.right, p)}px`
        markCenter(pill, p > 0.5 ? state.b : state.a)
      },
      end: (committed) => {
        const pill = pillRef.current
        const s = state
        state = null
        if (!pill || !s || committed) return
        pill.style.transition = `left .32s ${motion.navEase}, right .32s ${motion.navEase}, ${BASE}`
        pill.style.left = `${s.a.left}px`
        pill.style.right = `${s.a.right}px`
        markCenter(pill, s.a)
      },
    }
    return () => {
      previewRef.current = null
    }
  }, [previewRef, pillRef, itemOf])

  const barClass = [styles.bar, inline ? styles.inline : '', className ?? ''].filter(Boolean).join(' ')

  return (
    <nav className={barClass} style={NAV_VARS} aria-label="Основная навигация" data-inline={inline ? 'true' : 'false'}>
      <div ref={pillRef} className={styles.pill} />
      {items.map((item) => {
        const active = item.key === activeKey
        const tabClass = [styles.tab, item.center ? styles.center : '', active ? styles.on : ''].filter(Boolean).join(' ')
        const marker = item.marker && !(active && item.marker.kind === 'wait') ? item.marker : null
        const children = item.center ? (
          <span className={styles.fab}>
            <Icon name={item.icon} />
            {item.attention && <span className={styles.dot} aria-hidden="true" />}
          </span>
        ) : (
          <>
            {marker ? (
              <span className={styles.iconBox}>
                <Icon name={item.icon} />
                <TabMarker marker={marker} />
              </span>
            ) : (
              <Icon name={item.icon} />
            )}
            <span className={styles.label} aria-hidden="true">
              {item.label}
            </span>
          </>
        )
        return (
          <Fragment key={item.key}>
            {renderLink(item, {
              ref: register(item.key),
              className: tabClass,
              'aria-label': marker ? `${item.label}, ${marker.label}` : item.label,
              'aria-current': active ? 'page' : undefined,
              'data-center': item.center ? 'true' : 'false',
              children,
            })}
          </Fragment>
        )
      })}
    </nav>
  )
}
